import { DomainError } from '@/core/errors/DomainError';
import {
  formatIdSchema,
  matchIdSchema,
  roundKeySchema,
  type Match,
  type MatchId,
  type MatchSide,
  type PhaseId,
} from '@/core/schemas';

import type { MatchMutation, PhasePlan, PlannedMatch, Seed } from '../../contracts';
import { defineFormat } from '../../defineFormat';
import { winnerOf } from '../../matchResults';

import { matchesInRound, nextPowerOfTwo, roundCount, roundNaming, seedingOrder } from './bracket';
import { singleEliminationConfigSchema, type SingleEliminationConfig } from './config';

export const SINGLE_ELIMINATION_FORMAT_ID = formatIdSchema.parse('single-elimination');

const THIRD_PLACE_SLUG = 'spiel-um-platz-3';

function roundKeyFor(phaseId: PhaseId, slug: string) {
  return roundKeySchema.parse(`${phaseId}.${slug}`);
}

function matchIdFor(phaseId: PhaseId, round: number, index: number): MatchId {
  return matchIdSchema.parse(`${phaseId}-r${round + 1}-m${index + 1}`);
}

function thirdPlaceMatchId(phaseId: PhaseId): MatchId {
  return matchIdSchema.parse(`${phaseId}-${THIRD_PLACE_SLUG}`);
}

function resolveBracketSize(config: SingleEliminationConfig, participantCount: number): number {
  const size =
    config.bracketSize === 'auto' ? nextPowerOfTwo(participantCount) : config.bracketSize;

  if (size < participantCount) {
    throw new DomainError(
      'INVALID_PHASE_CONFIG',
      `Der Turnierbaum fasst ${size} Teams, es sind aber ${participantCount} qualifiziert.`,
      { bracketSize: size, participantCount },
    );
  }

  return size;
}

/** Fills the bracket positions with participants; empty positions are byes. */
function placeParticipants(
  participants: readonly Seed[],
  size: number,
  seeding: SingleEliminationConfig['seeding'],
): MatchSide[] {
  const bySeed = new Map<number, Seed>();
  participants.forEach((participant, index) => {
    bySeed.set(index + 1, participant);
  });

  const order =
    seeding === 'standard' ? seedingOrder(size) : Array.from({ length: size }, (_, i) => i + 1);

  return order.map((seedNumber) => {
    const participant = bySeed.get(seedNumber);
    return participant === undefined
      ? ({ kind: 'bye' } satisfies MatchSide)
      : ({ kind: 'team', teamId: participant.teamId } satisfies MatchSide);
  });
}

function pendingSide(matchId: MatchId, label: string): MatchSide {
  return { kind: 'pending', sourceRef: `winner:${matchId}`, label };
}

/**
 * Knockout bracket with seeding, automatic byes and progression.
 *
 * The whole tree is created up front, with undecided slots carried as
 * `pending`. That is what lets the bracket view show "Sieger VF 1" before a
 * single dart has been thrown.
 */
export const singleEliminationFormat = defineFormat<SingleEliminationConfig>({
  id: SINGLE_ELIMINATION_FORMAT_ID,
  label: 'KO-Runde',
  configSchema: singleEliminationConfigSchema,

  createPlan({ phase, config, participants }): PhasePlan {
    const size = resolveBracketSize(config, participants.length);
    const rounds = roundCount(size);
    const matches: PlannedMatch[] = [];

    let entries = placeParticipants(participants, size, config.seeding);
    let order = 0;

    for (let round = 0; round < rounds; round += 1) {
      const naming = roundNaming(size, round);
      const next: MatchSide[] = [];

      for (let index = 0; index < matchesInRound(size, round); index += 1) {
        const home = entries[index * 2] ?? { kind: 'bye' };
        const away = entries[index * 2 + 1] ?? { kind: 'bye' };

        const homeIsBye = home.kind === 'bye';
        const awayIsBye = away.kind === 'bye';

        // Two byes meet: nothing to play, the slot stays empty upwards.
        if (homeIsBye && awayIsBye) {
          next.push({ kind: 'bye' });
          continue;
        }

        // One bye and automatic advancement: no match is created at all,
        // which is how a bye works on paper.
        if ((homeIsBye || awayIsBye) && config.autoByes) {
          next.push(homeIsBye ? away : home);
          continue;
        }

        const matchId = matchIdFor(phase.id, round, index);

        matches.push({
          id: matchId,
          phaseId: phase.id,
          roundKey: roundKeyFor(phase.id, naming.slug),
          groupId: null,
          bracketSlot: { round, index },
          order: order++,
          home,
          away,
        });

        next.push(pendingSide(matchId, `Sieger ${naming.short} ${index + 1}`));
      }

      entries = next;
    }

    if (config.thirdPlaceMatch && rounds >= 2) {
      const semiNaming = roundNaming(size, rounds - 2);
      const first = matchIdFor(phase.id, rounds - 2, 0);
      const second = matchIdFor(phase.id, rounds - 2, 1);

      matches.push({
        id: thirdPlaceMatchId(phase.id),
        phaseId: phase.id,
        roundKey: roundKeyFor(phase.id, THIRD_PLACE_SLUG),
        groupId: null,
        bracketSlot: { round: rounds - 1, index: 1 },
        order,
        home: {
          kind: 'pending',
          sourceRef: `loser:${first}`,
          label: `Verlierer ${semiNaming.short} 1`,
        },
        away: {
          kind: 'pending',
          sourceRef: `loser:${second}`,
          label: `Verlierer ${semiNaming.short} 2`,
        },
      });
    }

    return { phaseId: phase.id, groups: [], matches };
  },

  /** A bracket has no table; the ranking is the bracket itself. */
  computeStandings() {
    return [];
  },

  /**
   * Recomputes every slot that a preceding match decides.
   *
   * Written as "recompute all derived slots" rather than "react to the match
   * that just finished": the result is the same after one match and
   * self-healing after a correction by the tournament director, a lost update
   * or a phase that was replanned.
   *
   * An already filled slot is included, which is what makes a correction
   * propagate — it keeps its `sourceRef`, so its origin is still known. Slots
   * filled before that field existed carry none and stay untouched.
   */
  resolveProgression({ phase, matches }): MatchMutation[] {
    const own = matches.filter((match) => match.phaseId === phase.id);
    const byId = new Map<MatchId, Match>(own.map((match) => [match.id, match]));
    const mutations: MatchMutation[] = [];

    for (const match of own) {
      for (const side of ['home', 'away'] as const) {
        const slot = match[side];
        const sourceRef = slot.kind === 'bye' ? undefined : slot.sourceRef;

        if (sourceRef === undefined) {
          continue;
        }

        const [role, sourceId] = sourceRef.split(':');
        const source = sourceId === undefined ? undefined : byId.get(sourceId as MatchId);

        if (source?.status !== 'completed') {
          continue;
        }

        const winner = winnerOf(source);
        if (winner === null) {
          continue; // A draw cannot decide a knockout slot.
        }

        let teamId = winner;

        if (role === 'loser') {
          const sides = [source.home, source.away]
            .filter((entry) => entry.kind === 'team')
            .map((entry) => entry.teamId);
          const loser = sides.find((candidate) => candidate !== winner);

          if (loser === undefined) {
            continue;
          }
          teamId = loser;
        }

        if (slot.kind === 'team' && slot.teamId === teamId) {
          continue; // Already correct — nothing to write.
        }

        mutations.push({
          matchId: match.id,
          side,
          participant: { kind: 'team', teamId, sourceRef },
        });
      }
    }

    return mutations;
  },

  /** The tournament winner, should a further phase ever follow. */
  selectQualifiers({ phase, matches }): Seed[] {
    const final = matches
      .filter((match) => match.phaseId === phase.id && match.bracketSlot !== null)
      .sort((a, b) => (b.bracketSlot?.round ?? 0) - (a.bracketSlot?.round ?? 0))
      .find((match) => match.bracketSlot?.index === 0);

    const winner = final === undefined ? null : winnerOf(final);

    return winner === null ? [] : [{ teamId: winner, rank: 1, label: 'Turniersieger' }];
  },

  isComplete({ phase, matches }): boolean {
    const own = matches.filter((match) => match.phaseId === phase.id);
    return own.length > 0 && own.every((match) => match.status === 'completed');
  },
});
